import React, { useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Avatar } from "./ui/avatar";
import { Badge } from "./ui/badge";
import { X, Send } from "lucide-react";
import { cn } from "../lib/utils";
import { useToast } from "./ui/toast";
import socketService from "../services/socket";
import { publicMeetingAPI } from "../services/api_v1";
import ChatMessage from "./ChatMessage";

interface PublicChatMessage {
	id: string;
	content: string;
	type: "text" | "system";
	sent_at: string;
	participant: {
		id: string;
		user: {
			id: string;
			first_name: string;
			last_name: string;
		};
	};
}

interface ChatPanelProps {
	meetingId: string;
	token: string;
	participantData: any;
	isChatOpen: boolean;
	setIsChatOpen: (open: boolean) => void;
	unreadMessageCount: number;
	setUnreadMessageCount: (count: number) => void;
	messages: PublicChatMessage[];
	setMessages: (messages: PublicChatMessage[]) => void;
	chatText: string;
	setChatText: (text: string) => void;
	isChatLoading: boolean;
	chatError: string;
	typingUsers: string[];
	setTypingUsers: (users: string[]) => void;
}

const ChatPanel: React.FC<ChatPanelProps> = ({
	meetingId,
	token,
	participantData,
	isChatOpen,
	setIsChatOpen,
	unreadMessageCount,
	setUnreadMessageCount,
	messages,
	setMessages,
	chatText,
	setChatText,
	isChatLoading,
	chatError,
	typingUsers,
	setTypingUsers,
}) => {
	const { addToast } = useToast();
	const chatScrollRef = useRef<HTMLDivElement>(null);
	const typingTimeout = useRef<number | null>(null);

	// Auto-scroll chat to bottom when messages change
	useEffect(() => {
		if (!chatScrollRef.current) return;
		chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
	}, [messages, isChatOpen]);

	// Use ref to track chat open state for socket listeners
	const isChatOpenRef = useRef(isChatOpen);
	useEffect(() => {
		isChatOpenRef.current = isChatOpen;
	}, [isChatOpen]);

	// Reset unread count when chat is opened
	useEffect(() => {
		if (isChatOpen) {
			setUnreadMessageCount(0);
		}
	}, [isChatOpen]);

	const fetchChatHistory = async () => {
		if (!meetingId || !token) return;

		try {
			// setIsChatLoading(true);
			// setChatError("");

			const response = await publicMeetingAPI.getMessages(meetingId, token);

			if (response.data.success) {
				setMessages(response.data.data.messages || []);
			} else {
				// setChatError("Failed to load message history");
			}
		} catch (error: any) {
			console.error("Failed to fetch message history:", error);
			// setChatError(error.response?.data?.message || "Failed to load messages");
		} finally {
			// setIsChatLoading(false);
		}
	};

	const handleChatSend = async () => {
		const content = chatText.trim();
		if (!content || !meetingId || !token) return;

		try {
			console.log("[ChatPanel] Sending message:", {
				meetingId,
				content,
			});
			const response = await publicMeetingAPI.sendMessage(meetingId, token, {
				content,
				type: "text",
			});

			console.log(
				"[ChatPanel] Message sent successfully:",
				response.data,
			);

			// Don't add message to local state - let socket listener handle it
			// This prevents duplicate messages for the sender

			setChatText("");
			socketService.stopTyping(meetingId);
		} catch (error: any) {
			console.error("[ChatPanel] Failed to send message:", error);
			// setChatError(error.response?.data?.message || "Failed to send message");
		}
	};

	// Cleanup typing timeout on unmount
	useEffect(() => {
		return () => {
			if (typingTimeout.current) {
				window.clearTimeout(typingTimeout.current);
			}
		};
	}, []);

	const formatFullDate = (dateString: string) => {
		const date = new Date(dateString);
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, "0");
		const day = String(date.getDate()).padStart(2, "0");
		const hours = String(date.getHours()).padStart(2, "0");
		const minutes = String(date.getMinutes()).padStart(2, "0");
		const ampm = date.getHours() >= 12 ? "PM" : "AM";
		return `${year}-${month}-${day} ${hours}:${minutes} ${ampm}`;
	};

	const formatRelativeTime = (dateString: string) => {
		const date = new Date(dateString);
		const now = new Date();
		const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

		if (diffInSeconds < 60) {
			return "just now";
		}

		const diffInMinutes = Math.floor(diffInSeconds / 60);
		if (diffInMinutes < 60) {
			return `${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""} ago`;
		}

		const diffInHours = Math.floor(diffInMinutes / 60);
		if (diffInHours < 24) {
			return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
		}

		const diffInDays = Math.floor(diffInHours / 24);
		return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
	};

	// Function to detect text direction
	const getTextDirection = (text: string): "rtl" | "ltr" => {
		const rtlRegex = /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/;
		return rtlRegex.test(text) ? "rtl" : "ltr";
	};

	const inputDirection = getTextDirection(chatText);

	return (
		<div
			className={cn(
				"z-50 fixed top-0 right-0 h-full w-full sm:w-96 bg-background/95 backdrop-blur-xl border-l border-border shadow-2xl transition-transform duration-300 theme-transition",
				isChatOpen ? "translate-x-0" : "translate-x-full",
				"flex flex-col rounded-l-xl overflow-hidden",
			)}
			role="complementary"
			aria-label="Meeting chat panel"
		>
			{/* Header */}
			<div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card/50">
				<div>
					<h3 className="text-sm font-semibold text-foreground">
						Meeting Chat
					</h3>
					<p className="text-xs text-muted-foreground">
						Visible to everyone in the meeting
					</p>
				</div>
				<Button
					variant="ghost"
					size="icon"
					onClick={() => setIsChatOpen(false)}
					aria-label="Close chat"
					className="text-foreground hover:bg-accent"
				>
					<X className="h-4 w-4" />
				</Button>
			</div>

			{/* Messages */}
			<div
				ref={chatScrollRef}
				className="flex-1 overflow-y-auto p-4 space-y-4"
			>
				{isChatLoading && (
					<div className="flex items-center justify-center py-4">
						<div className="text-sm text-muted-foreground">
							Loading messages...
						</div>
					</div>
				)}

				{chatError && (
					<div className="flex items-center justify-center py-4">
						<div className="text-sm text-destructive">
							{chatError}
						</div>
					</div>
				)}

				{!isChatLoading &&
					!chatError &&
					messages.map((msg, idx) => {
						const firstName = msg.participant.user.first_name;
						const lastName = msg.participant.user.last_name === "User" ? "" : msg.participant.user.last_name;
						const displayName = `${firstName} ${lastName}`.trim() || "Unknown User";
						const fallback = displayName
							? displayName.charAt(0).toUpperCase()
							: "U";

						// Check if this message is from the current user
						const isCurrentUser =
							msg.participant.user.id ===
							participantData?.participant?.user_id;

						return (
							<div
								key={`${msg.id || "local"}-${idx}`}
								className={`flex items-start space-x-3 ${isCurrentUser ? "flex-row-reverse space-x-reverse" : ""}`}
							>
								<Avatar fallback={fallback} size="sm" />
								<div
									className={`max-w-[80%] ${isCurrentUser ? "text-right" : "text-left"}`}
								>
									{isCurrentUser && (
										<div className="flex items-center justify-between gap-4">
											<span
												className="text-xs text-muted-foreground cursor-help"
												title={formatFullDate(msg.sent_at)}
											>
												{formatRelativeTime(msg.sent_at)}
											</span>

											<span className="text-sm font-medium text-foreground text-right">
												{displayName || "You"}
											</span>
										</div>
									)}

									{!isCurrentUser && (
										<div className="flex items-center justify-between gap-4">
											<span className="text-sm font-medium text-foreground">
												{displayName || "Unknown"}
											</span>

											<span
												className="text-xs text-muted-foreground cursor-help"
												title={formatFullDate(msg.sent_at)}
											>
												{formatRelativeTime(msg.sent_at)}
											</span>
										</div>
									)}

									<ChatMessage
										content={msg.content}
										className={`mt-1 text-sm rounded-md px-3 py-2 max-w-[20rem] break-words ${isCurrentUser
											? "bg-primary text-primary-foreground ml-auto"
											: "bg-muted text-foreground"
											}`}
										linkClassName={isCurrentUser ? "text-primary-foreground underline hover:opacity-80" : "text-blue-500 hover:underline"}
									/>
								</div>
							</div>
						);
					})}
			</div>

			{/* Typing indicator */}
			{typingUsers.length > 0 && (
				<div className="px-4 pb-2 text-xs text-muted-foreground">
					{typingUsers.length === 1
						? `${typingUsers[0]} is typing...`
						: typingUsers.length === 2
							? `${typingUsers[0]} and ${typingUsers[1]} are typing...`
							: "Several people are typing..."}
				</div>
			)}

			{/* Composer */}
			<div className="p-3 border-t border-border bg-card/50">
				<div className="flex items-center space-x-2">
					<div className="flex-1">
						<Input
							value={chatText}
							dir={inputDirection}
							onChange={(e) => {
								setChatText(e.target.value);
								// Handle typing indicator
								socketService.startTyping(meetingId);
								if (typingTimeout.current)
									clearTimeout(typingTimeout.current);
								typingTimeout.current = window.setTimeout(
									() => socketService.stopTyping(meetingId),
									1500,
								);
							}}
							placeholder="Send a message to everyone"
							className="flex-1"
							onKeyDown={(e) => {
								if (e.key === "Enter" && !e.shiftKey) {
									e.preventDefault();
									handleChatSend();
								}
							}}
							aria-label="Message input"
						/>
					</div>
					<Button
						variant="default"
						onClick={handleChatSend}
						aria-label="Send message"
						className="bg-primary text-primary-foreground hover:bg-primary/90"
					>
						<Send className="h-4 w-4" />
					</Button>
				</div>
			</div>
		</div>
	);
};

export default ChatPanel;